import { FC, Fragment, useReducer } from 'react';

import { useFormik } from 'formik';
import { FolderPlus } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { FolderForm } from '../../form';

import { fileSystemService } from 'v2/api/fileSystemService';
import { handleHttpError } from 'v2/utils';

interface CreateRootFolderButtonProps {
  onSuccess: VoidFunction;
}

export const CreateRootFolderButton: FC<CreateRootFolderButtonProps> = ({ onSuccess }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger, isMutating } = useSWRMutation('createFolder', fileSystemService.createFolder, {
    onSuccess: () => {
      onSuccess();
      toast.success('The folder has been successfully created');
      handleClose();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<FolderForm>({
    initialValues: { name: '', description: '' },
    onSubmit: (values) => {
      trigger(values);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setOpen();
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<FolderPlus width={20} />} variant="contained" size="large">
        Create Root Folder
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Create Folder</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent className="form">
            <TextField
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Name"
              required
              fullWidth
            />

            <TextField
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Description"
              multiline
              rows={4}
              fullWidth
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button type="submit" variant="contained" loading={isMutating}>
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
